/**
 * Which language an entry's text leads with (2026-09-28, Teo: "Czech chrome on cz/docs"):
 * the page's. On /cz/docs an entry with `titleCs` / `descriptionCs` shows the Czech text
 * first and the Slovenian as the secondary, labelled block (»Slovinsky«); on /si/docs it
 * is the other way round (»Česky«). An entry with one language only reads the same on
 * both halves. Pure — the renderers (renderSection.ts, renderKatalog.ts) turn it into HTML.
 *
 * The primary block carries no `lang` attribute (it inherits the page's): the seeded
 * Czech KingCAD layers hold Czech text in the Slovenian columns, so stamping `sl` on a
 * primary read from those columns would be wrong as often as right. The alt block always
 * carries its language.
 */

import type { WikiEntry } from '../../shared/types/layerWiki.js';
import type { DocsLanguage } from './docsCopy.js';

export interface BilingualText {
  /** The title shown (the h1 / a section's h2). */
  title: string;
  /** The other language's title, or null when the entry has one language only. */
  altTitle: string | null;
  /** The description shown first; null when the entry has no text at all. */
  desc: string | null;
  /** The other language's description — a labelled block under `desc`; null when none. */
  altDesc: string | null;
  /** The language of the alt title / block (its `lang` attribute). */
  altLang: DocsLanguage;
}

export function bilingual(e: WikiEntry, lang: DocsLanguage): BilingualText {
  if (lang === 'cs' && (e.titleCs || e.descriptionCs)) {
    return {
      title: e.titleCs ?? e.title,
      altTitle: e.titleCs ? e.title : null,
      desc: e.descriptionCs || e.description || null,
      altDesc: e.descriptionCs && e.description ? e.description : null,
      altLang: 'sl',
    };
  }
  return {
    title: e.title,
    altTitle: e.titleCs,
    desc: e.description || null,
    altDesc: e.descriptionCs || null,
    altLang: 'cs',
  };
}
